/**
 * AdminCompliancePage — Hub Compliance IVASS / AML / DORA.
 *
 * Route: /admin/compliance (+ alias /admin/compliance/aml|tech-providers|funzione-aml)
 *
 * 3 tab:
 *   - AML sospetti (lista lead vita con scoring rischio + Segnala SOS)
 *   - Funzione AML (settings nome responsabile, formazione)
 *   - Tech Providers (DORA-lite + aggiunta dinamica)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast as sonnerToast } from "sonner";
import { Shield, AlertTriangle, Server, FileCheck, RefreshCw, Download, X, Plus, Save } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminCompliancePage() {
  const [tab, setTab] = useState("aml");
  const [aml, setAml] = useState({ leads_a_rischio: [], sospetti_segnalati: [], loading: true });
  const [providers, setProviders] = useState({ items: [], loading: true });
  const [funzione, setFunzione] = useState({ loading: true });
  const [modalFunzione, setModalFunzione] = useState(false);
  const [modalProvider, setModalProvider] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => { reloadAll(); }, []);

  const reloadAll = async () => {
    setAml(s => ({ ...s, loading: true }));
    setProviders(s => ({ ...s, loading: true }));
    setFunzione({ loading: true });
    try {
      const [r1, r2, r3] = await Promise.all([
        axios.get(`${API}/api/admin/compliance/aml/sospetti`, { withCredentials: true }),
        axios.get(`${API}/api/admin/compliance/tech-providers`, { withCredentials: true }),
        axios.get(`${API}/api/admin/compliance/aml/funzione`, { withCredentials: true }),
      ]);
      setAml({ ...r1.data, loading: false });
      setProviders({ items: r2.data?.providers || [], loading: false });
      setFunzione({ ...r3.data, loading: false });
    } catch (e) {
      setAml(s => ({ ...s, loading: false }));
      setProviders(s => ({ ...s, loading: false }));
      setFunzione({ loading: false });
    }
  };

  const handleSegnalaSos = async (lead) => {
    try {
      await axios.post(`${API}/api/admin/compliance/aml/sospetti/segnala`, {
        lead_id: lead.id,
        motivo: (lead.motivi || ["Manuale dall'admin"]).join(" · "),
        rischio: lead.rischio || "medio",
      }, { withCredentials: true });
      setToast({ type: "ok", msg: `SOS registrato per ${lead.anagrafica_nome}. UIF: placeholder integrazione futura.` });
      reloadAll();
    } catch (e) {
      setToast({ type: "err", msg: "Errore registrazione SOS" });
    }
    setTimeout(() => setToast(null), 5000);
  };

  return (
    <div className="min-h-screen bg-zinc-50 pt-24 pb-12" data-testid="admin-compliance-page">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-start gap-3 mb-8">
          <Shield className="w-8 h-8 text-emerald-700 mt-1" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900">Compliance Hub</h1>
            <p className="text-sm text-zinc-500 mt-1">AML antiriciclaggio · Funzione AML · DORA tech providers · IVASS audit</p>
          </div>
          <button onClick={reloadAll} className="ml-auto inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-semibold bg-white border border-zinc-200 hover:bg-zinc-50" data-testid="compl-reload">
            <RefreshCw className="w-3.5 h-3.5" /> Aggiorna
          </button>
        </div>

        {toast && (
          <div className={`mb-4 px-4 py-3 rounded-xl text-sm ${toast.type === "ok" ? "bg-emerald-50 border border-emerald-200 text-emerald-900" : "bg-red-50 border border-red-200 text-red-900"}`} data-testid="compl-toast">
            {toast.msg}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-6 border-b border-zinc-200">
          {[
            { id: "aml", label: "AML / Antiriciclaggio", icon: AlertTriangle },
            { id: "funzione", label: "Funzione AML", icon: FileCheck },
            { id: "providers", label: "Tech Providers (DORA)", icon: Server },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id ? "border-emerald-700 text-emerald-700" : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`}
              data-testid={`compl-tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "aml" && (
          <div data-testid="aml-panel">
            <h2 className="font-semibold text-zinc-900 mb-3">Lead vita con punteggio rischio</h2>
            {aml.loading ? <p className="text-sm text-zinc-500">Caricamento…</p> : (
              <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                    <tr><th className="text-left p-3">Lead</th><th className="text-left p-3">Prodotto</th><th className="text-left p-3">Valore</th><th className="text-left p-3">Rischio</th><th className="text-left p-3">Motivi</th><th className="text-left p-3">Azioni</th></tr>
                  </thead>
                  <tbody>
                    {aml.leads_a_rischio?.length === 0 ? (
                      <tr><td colSpan="6" className="p-6 text-center text-zinc-500">Nessun lead vita da valutare.</td></tr>
                    ) : aml.leads_a_rischio.map((l, i) => (
                      <tr key={i} className="border-t border-zinc-100" data-testid={`aml-lead-row-${i}`}>
                        <td className="p-3">{l.anagrafica_nome}</td>
                        <td className="p-3">{l.prodotto}</td>
                        <td className="p-3">{l.valore ? `€ ${l.valore}` : "—"}</td>
                        <td className="p-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                            l.rischio === "alto" ? "bg-red-100 text-red-700" :
                            l.rischio === "medio" ? "bg-amber-100 text-amber-700" :
                            "bg-emerald-100 text-emerald-700"
                          }`}>{l.rischio}</span>
                        </td>
                        <td className="p-3 text-xs text-zinc-600">{(l.motivi || []).join(" · ") || "—"}</td>
                        <td className="p-3">
                          <button onClick={() => handleSegnalaSos(l)} className="text-xs px-3 py-1.5 rounded-full bg-red-50 text-red-700 hover:bg-red-100 font-semibold" data-testid={`aml-flag-${i}`}>Segnala SOS</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {aml.sospetti_segnalati?.length > 0 && (
              <>
                <h3 className="font-semibold text-zinc-900 mt-8 mb-3">SOS già registrati</h3>
                <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                      <tr><th className="text-left p-3">Lead ID</th><th className="text-left p-3">Motivo</th><th className="text-left p-3">Rischio</th><th className="text-left p-3">Data</th><th className="text-left p-3">UIF</th></tr>
                    </thead>
                    <tbody>
                      {aml.sospetti_segnalati.map((s, i) => (
                        <tr key={i} className="border-t border-zinc-100">
                          <td className="p-3 text-xs font-mono">{s.lead_id || "—"}</td>
                          <td className="p-3 text-xs">{s.motivo}</td>
                          <td className="p-3 text-xs uppercase font-bold">{s.rischio}</td>
                          <td className="p-3 text-xs">{s.created_at?.slice(0, 10)}</td>
                          <td className="p-3 text-xs">{s.segnalata_uif ? "✓ Inviata" : "TODO"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <p className="text-xs text-zinc-400 mt-3" data-needs-review="true">
              ⚠️ <strong>TODO</strong>: integrazione con UIF infostat per invio segnalazione SOS effettiva. Oggi i SOS sono salvati solo in <code>aml_sospetti</code>.
            </p>
          </div>
        )}

        {tab === "funzione" && (
          <div className="bg-white rounded-2xl border border-zinc-200 p-6 space-y-4" data-testid="funzione-aml-panel">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-zinc-900">Funzione antiriciclaggio designata</h2>
              <button onClick={() => setModalFunzione(true)} className="text-xs px-3 py-1.5 rounded-full bg-zinc-900 text-white hover:bg-zinc-800 font-semibold inline-flex items-center gap-1" data-testid="aml-edit-funzione">
                <Save className="w-3 h-3" /> Aggiorna
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <FunzioneField label="Responsabile funzione AML" value={funzione.responsabile_nome} testid="fz-resp" />
              <FunzioneField label="Sostituto" value={funzione.sostituto_nome} testid="fz-sost" />
              <FunzioneField label="Ultima formazione" value={funzione.ultima_formazione_data} testid="fz-form-ultima" />
              <FunzioneField label="Prossima scadenza formazione" value={funzione.prossima_formazione_data} testid="fz-form-prossima" />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-900" data-needs-review="true">
              <strong>Onere di prova IVASS</strong>: mantenere registro annuale formazione AML (Reg. IVASS 44/2019). Documenti da archiviare: attestati, programma del corso, valutazione annuale dei rischi.
            </div>
            <button
              onClick={() => sonnerToast.info("Export registro AML in arrivo — funzionalità pianificata (Reg. IVASS 44/2019). Contatta lo staff Colucci per la generazione manuale del registro.")}
              className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-zinc-900 text-white text-sm font-semibold"
              data-testid="aml-export-registry"
            >
              <Download className="w-4 h-4" /> Esporta registro AML (PDF)
            </button>
          </div>
        )}

        {tab === "providers" && (
          <div data-testid="providers-panel">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-zinc-900">Provider tecnologici (registro DORA-lite)</h2>
              <button onClick={() => setModalProvider(true)} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 font-semibold" data-testid="provider-add-btn">
                <Plus className="w-3 h-3" /> Aggiungi provider
              </button>
            </div>
            {providers.loading ? <p className="text-sm text-zinc-500">Caricamento…</p> : (
              <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                    <tr><th className="text-left p-3">Provider</th><th className="text-left p-3">Finalità</th><th className="text-left p-3">Dati trattati</th><th className="text-left p-3">DPA</th><th className="text-left p-3">Stato</th></tr>
                  </thead>
                  <tbody>
                    {providers.items.map((p, i) => (
                      <tr key={p.slug} className="border-t border-zinc-100" data-testid={`provider-row-${p.slug}`}>
                        <td className="p-3 font-semibold">{p.nome}</td>
                        <td className="p-3 text-zinc-600">{p.finalita}</td>
                        <td className="p-3 text-xs text-zinc-500">{p.dati_trattati}</td>
                        <td className="p-3">{p.dpa_url ? <a href={p.dpa_url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline text-xs">Vedi</a> : <span className="text-xs text-zinc-400" data-needs-review="true">DPA mancante</span>}</td>
                        <td className="p-3"><span className={`text-[10px] uppercase font-bold ${p.stato === "attivo" ? "text-emerald-700" : p.stato === "valutazione" ? "text-amber-700" : "text-zinc-400"}`}>{p.stato}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {modalFunzione && (
          <FunzioneAmlModal initial={funzione} onClose={() => setModalFunzione(false)} onSaved={() => { setModalFunzione(false); reloadAll(); }} />
        )}
        {modalProvider && (
          <AddProviderModal onClose={() => setModalProvider(false)} onSaved={() => { setModalProvider(false); reloadAll(); }} />
        )}
      </div>
    </div>
  );
}

function FunzioneField({ label, value, testid }) {
  return (
    <div>
      <p className="text-xs uppercase text-zinc-500 mb-1">{label}</p>
      <p className={value ? "text-zinc-900" : "text-amber-700"} data-testid={testid} data-needs-review={value ? undefined : "true"}>
        {value || "[DA NOMINARE]"}
      </p>
    </div>
  );
}

function FunzioneAmlModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    responsabile_nome: initial?.responsabile_nome || "",
    sostituto_nome: initial?.sostituto_nome || "",
    ultima_formazione_data: initial?.ultima_formazione_data || "",
    prossima_formazione_data: initial?.prossima_formazione_data || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/compliance/aml/funzione`, form, { withCredentials: true });
      onSaved?.();
    } catch (_) { /* TODO toast errore */ }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="modal-funzione-aml">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-lg">Aggiorna funzione AML</h3>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded-full"><X className="w-4 h-4" /></button>
        </div>
        {[
          ["responsabile_nome", "Nome responsabile funzione AML", "text"],
          ["sostituto_nome", "Sostituto", "text"],
          ["ultima_formazione_data", "Data ultima formazione", "date"],
          ["prossima_formazione_data", "Data prossima formazione", "date"],
        ].map(([k, label, type]) => (
          <label key={k} className="block">
            <span className="text-xs uppercase text-zinc-500">{label}</span>
            <input type={type} value={form[k]} onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))} className="mt-1 w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm" data-testid={`modal-funzione-${k}`} />
          </label>
        ))}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full border border-zinc-200 text-sm">Annulla</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-full bg-emerald-700 text-white text-sm font-semibold" data-testid="modal-funzione-save">{saving ? "Salvataggio…" : "Salva"}</button>
        </div>
      </div>
    </div>
  );
}

function AddProviderModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ slug: "", nome: "", finalita: "", dati_trattati: "", dpa_url: "", stato: "valutazione" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.slug || !form.nome) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/compliance/tech-providers`, form, { withCredentials: true });
      onSaved?.();
    } catch (_) { /* TODO toast errore */ }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="modal-add-provider">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-lg">Aggiungi tech provider</h3>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded-full"><X className="w-4 h-4" /></button>
        </div>
        {[
          ["slug", "Slug (es. clarity-mailer)"],
          ["nome", "Nome commerciale"],
          ["finalita", "Finalità"],
          ["dati_trattati", "Dati trattati"],
          ["dpa_url", "URL DPA (opzionale)"],
        ].map(([k, label]) => (
          <label key={k} className="block">
            <span className="text-xs uppercase text-zinc-500">{label}</span>
            <input value={form[k]} onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))} className="mt-1 w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm" data-testid={`modal-provider-${k}`} />
          </label>
        ))}
        <label className="block">
          <span className="text-xs uppercase text-zinc-500">Stato</span>
          <select value={form.stato} onChange={(e) => setForm(f => ({ ...f, stato: e.target.value }))} className="mt-1 w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm">
            <option value="attivo">attivo</option>
            <option value="valutazione">valutazione</option>
            <option value="dismesso">dismesso</option>
          </select>
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full border border-zinc-200 text-sm">Annulla</button>
          <button onClick={save} disabled={saving || !form.slug || !form.nome} className="px-4 py-2 rounded-full bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50" data-testid="modal-provider-save">{saving ? "Salvataggio…" : "Aggiungi"}</button>
        </div>
      </div>
    </div>
  );
}
